import type { LayoutType } from './types'

/**
 * Режимы студии макетов и состав фиксированного режима «18» (внутренний id fixed14).
 *
 * Источник истины по составу — гейтвей: `src/services/layout-studio-modes.ts`
 * (`FIXED14_COMPOSITION`). Фронт собирается отдельным Vite-проектом со своим
 * tsconfig (`include: ["src"]` только внутри `frontend/`), прямого пути к файлу
 * гейтвея у бандлера нет и заводить его ради одной константы — лишний риск.
 * Поэтому состав продублирован здесь; строка-в-строку совпадает с гейтвеем,
 * при изменении состава «14» надо поправить оба места.
 */
export const STUDIO_MODES = ['free', 'fixed14'] as const
export type StudioMode = (typeof STUDIO_MODES)[number]

export const DEFAULT_STUDIO_MODE: StudioMode = 'free'

export function isStudioMode(value: unknown): value is StudioMode {
  return typeof value === 'string' && (STUDIO_MODES as readonly string[]).includes(value)
}

/** См. FIXED14_COMPOSITION в src/services/layout-studio-modes.ts — держать в синхроне. */
export const FIXED14_COMPOSITION: ReadonlyArray<{ type: LayoutType; count: number }> = [
  { type: 'circle', count: 6 },
  { type: 'bento', count: 6 },
  { type: 'topper', count: 2 },
  { type: 'cupcake_5', count: 2 },
  { type: 'cupcake_6', count: 2 },
]

export const FIXED14_TOTAL_SHEETS = FIXED14_COMPOSITION.reduce((sum, row) => sum + row.count, 0)

/** Число кругов серии при едином пути консилиума (SPEC_2026-10-25 §3.1, §9): 1–12, по умолчанию 6. */
export const CONSILIUM_CIRCLES_MIN = 1
export const CONSILIUM_CIRCLES_MAX = 12
export const CONSILIUM_CIRCLES_DEFAULT = 6

/** Число кругов, приведённое к допустимому диапазону; не число — по умолчанию. */
export function clampCircleCount(value: unknown): number {
  const n = Math.round(Number(value))
  if (!Number.isFinite(n)) return CONSILIUM_CIRCLES_DEFAULT
  return Math.min(CONSILIUM_CIRCLES_MAX, Math.max(CONSILIUM_CIRCLES_MIN, n))
}

/**
 * Состав серии консилиума (SPEC_2026-10-25 §16.6): круги и бенто — по N (каждый круг даёт `circle n` и `bento n`),
 * топперы и капкейки — как в `FIXED14_COMPOSITION` (2 + 2 + 2).
 */
export function consiliumComposition(circleCount: number): Array<{ type: LayoutType; count: number }> {
  const n = clampCircleCount(circleCount)
  return FIXED14_COMPOSITION.map(({ type, count }) => ({ type, count: type === 'circle' || type === 'bento' ? n : count }))
}

/** Число макетов серии консилиума: N кругов + N бенто + 2 + 2 + 2. */
export const consiliumTotalSheets = (circleCount: number): number => consiliumComposition(circleCount).reduce((sum, row) => sum + row.count, 0)

/** Число кругов из состава черновика (строка `circle`); нет строки или значение вне диапазона — по умолчанию. */
export function circleCountOfComposition(composition: ReadonlyArray<{ type: string; enabled?: boolean; count: number }> | undefined): number {
  const row = composition?.find((item) => item.type === 'circle' && item.enabled !== false)
  return row ? clampCircleCount(row.count) : CONSILIUM_CIRCLES_DEFAULT
}

/**
 * Источник картинок «Студии — 18» (docs/SPEC_2026-09-23_LAYOUT_STUDIO_MANUAL_MODES.md §3.1,
 * §3.2). Состав серии («18») один и тот же для всех трёх вариантов — меняется только
 * то, кто делает картинки. Копия `StudioInputMode` в `src/services/layout-studio-modes.ts`
 * (гейтвей) — держать в синхроне.
 */
export const STUDIO_INPUT_MODES = ['ai', 'manual_prompts', 'manual_images'] as const
export type StudioInputMode = (typeof STUDIO_INPUT_MODES)[number]

export const DEFAULT_STUDIO_INPUT_MODE: StudioInputMode = 'ai'

export function isStudioInputMode(value: unknown): value is StudioInputMode {
  return typeof value === 'string' && (STUDIO_INPUT_MODES as readonly string[]).includes(value)
}
